import { StrictMode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'

import { type MigrationMode, useMigrationGuardWithMode } from '@dhl/domain-migration'
import { I18nProvider } from '@dhl/i18n'
import { setupLocalXsrfRequestInterceptor, useSetupLocalXsrfRequestInterceptor } from '@dhl/receiver-axios'
import { CacheProvider, ThemeProvider } from '@dhl/theme'
import { initDataLayer } from '@dhl/third-party'
import { VersionProvider } from '@dhl/version-provider'

import { StartUp } from '~/StartUp'

import { AccountNativeBridge } from './AccountNativeBridge'
import { getConfig } from './config'
import { queryClient } from './query-client'
import { Routes } from './Routes'
import { ThemeOverride } from './ThemeOverride'
import { useAuthRedirect } from './useAuthRedirect'
import { useReferrer } from './useReferrer'
import { useSetupRequestInterceptor } from './interceptor'

setupLocalXsrfRequestInterceptor(process.env.REACT_APP_ENVIRONMENT)

initDataLayer()

const InnerApp = () => {
  useReferrer()
  useAuthRedirect()
  useSetupLocalXsrfRequestInterceptor(process.env.REACT_APP_ENVIRONMENT)
  useSetupRequestInterceptor()

  const { migration } = getConfig()
  const { continueRenderApp } = useMigrationGuardWithMode(migration.mode as MigrationMode)

  if (!continueRenderApp) {
    return null
  }

  const { basePath, cspNonce, version } = getConfig()
  const baseNameWithoutTrailingSlash = basePath.slice(0, -1)

  return (
    <CacheProvider nonce={cspNonce}>
      <ThemeProvider>
        <ThemeOverride>
          <I18nProvider>
            <BrowserRouter
              basename={baseNameWithoutTrailingSlash}
              future={{
                v7_relativeSplatPath: true,
                v7_startTransition: false,
              }}
            >
              <VersionProvider url={`${basePath}version.json`} version={version}>
                <AccountNativeBridge>
                  <StartUp>
                    <StrictMode>
                      <Routes />
                    </StrictMode>
                  </StartUp>
                </AccountNativeBridge>
              </VersionProvider>
            </BrowserRouter>
          </I18nProvider>
        </ThemeOverride>
      </ThemeProvider>
    </CacheProvider>
  )
}

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <InnerApp />
  </QueryClientProvider>
)
